import { useMediaQuery, useTheme, Box } from '@mui/material'
import { Auth } from 'aws-amplify'
import idDirectory from './idAttributes'
import {
    DisputeDeflectorBanner,
    TopBar,
    StandardFooter,
    Sidebar,
    ErrorBoundary,
    SabreTermsAndConditionsModal,
} from 'components'
import NavLinks from 'NavLinks'
import NavLinksNewGrids from 'NavLinksNewGrids'
import NavLinksIssuer from 'NavLinksIssuer'
import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import logger from 'utils/logger'
import { SidebarNav } from 'components/SidebarNav'
import {
    clearLocalStorage,
    useAuthedUser,
} from 'context/AuthedUser/AuthedUserContext'
import CB from 'lib'
import {
    CLIENT_THEME_VARIANT,
} from 'hooks/useThemeStyling/useThemeStyling'
import VASBanner from 'components/VASBanner'
import useFeatureToggle from 'hooks/FeatureToggles/useFeatureToggles'
import SidebarAccountSwitcher from 'components/SidebarAccountSwitcher/SidebarAccountSwitcher'

export interface ILayoutMProps {
    children: React.ReactNode
    className?: string
    containerProps?: unknown
}

/** TODO: Reafact checklist
 *
 * - [x] Create Set component for wrapping (layouts, contexts, etc...)
 * - [x] Crate MainLayout component
 * - [x] Update children that are dependent on props so they get their own data (sidebar, etc)
 * - [] Update Set to help with role based route access. This should probably be done in the views with a useRequireRole(<Role>) hook
 * */

const MainLayout: React.FC = ({ children }) => {
    const theme = useTheme()
    
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'), {
        defaultMatches: true,
    })
    const { pathname } = useLocation()
    const { enabled: footerEnabled } = useFeatureToggle('MAIN_FOOTER')

    const [pageTitle, setPageTitle] = useState<string>('Dashboard')
    // Always open by default on desktop
    const [openSidebar, setOpenSidebar] = useState(true)

    const handleSidebarOpen = () => {
        setOpenSidebar(true)
    }

    const handleSidebarClose = () => {
        setOpenSidebar(false)
    }
    const {
        selectedPatchIDs,
        setSelectedPatchIDs,
        selectedPatchType,
        setSelectedPatchType,
        hasClearedCaseStatus,
        setHasClearedCaseStatus,
        SSOTokenURL,
        showNewDataGrid,
        user,
    } = useAuthedUser()

    const themeVariantId = user?.theme_variant ?? 0

    const showClientTypeIssuerUI = user?.is_issuer ?? false

    const currentNavLinks = showClientTypeIssuerUI
        ? NavLinksIssuer
        : showNewDataGrid
        ? NavLinksNewGrids
        : NavLinks

    // Needed to set initial page title if page is reloaded.
    useEffect(() => {
        currentNavLinks.forEach((section) =>
            section.sublinks
                ? section.sublinks.forEach(
                      (subSection) =>
                          subSection.href === pathname &&
                          setPageTitle(subSection.title)
                  )
                : section.href === pathname && setPageTitle(section.title)
        )
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const isSidebarCurrentlyOpen = isDesktop ? true : openSidebar
    const handleSignout = async () => {
        try {
            const localStorageIsSAMLBackDoor = localStorage.getItem(
                'isSAMLBackDoor'
            )
            if (selectedPatchIDs && !hasClearedCaseStatus) {
                setHasClearedCaseStatus && setHasClearedCaseStatus(true)
                const actionId = selectedPatchType === 'upload' ? 1 : 2
                CB.cases
                    .callCasePatch(
                        selectedPatchIDs,
                        actionId,
                        selectedPatchType
                    )
                    .then(() => {
                        setSelectedPatchIDs && setSelectedPatchIDs('')
                        setSelectedPatchType && setSelectedPatchType('')
                    })
                    .catch(() => {})
                    .finally(async () => {
                        await Auth.signOut().then(() => {
                            clearLocalStorage()
                            if (SSOTokenURL?.length) {
                                const tempURL = SSOTokenURL
                                localStorage.removeItem('SSOTokenURL')
                                return (document.location.href = tempURL)
                            }
                            document.location.href = `/${
                                localStorageIsSAMLBackDoor
                                    ? '?saml_back_door_logout=1'
                                    : ''
                            }`
                        })
                    })
            } else {
                await Auth.signOut().then(() => {
                    clearLocalStorage()
                    if (SSOTokenURL?.length) {
                        const tempURL = SSOTokenURL
                        localStorage.removeItem('SSOTokenURL')
                        return (document.location.href = tempURL)
                    }
                    document.location.href = `/${
                        localStorageIsSAMLBackDoor
                            ? '?saml_back_door_logout=1'
                            : ''
                    }`
                })
            }
        } catch (error) {
            logger('Error signing out: ', error)
        }
    }

    useEffect(() => {
        if (!isDesktop) {
            handleSidebarClose()
        }
    }, [isDesktop])

    /* Show on any page (except terms of use and privacy policy) and only show modal for Sabre issuer and merchant (not sabre_admin user): */
    const showTermsAndConditionsModal =
        [7].includes(themeVariantId) &&
        !['/terms-of-use', '/privacy-policy', '/dpa'].includes(pathname) &&
        user?.personRepresentation() !== 'sabre_admin'

    const showSidebarAccountSwitcher = [
        'sabre_admin', // ID: 35
        'sabre-partner-admin', // ID: 68
        'superadmin_user', // ID: 88
        'partneradmin_user', // ID: 72
    ].includes(user?.personRepresentation() ?? '')
     

    return (
        <div>
            <div
                id={idDirectory.divRoot}
                className={`layout-m ${'emp-mainLayout-root'}`}
                style={{
                    paddingBottom: footerEnabled ? 26 : 0,
                }}
            >
                <header id={idDirectory.header} className="m-header">
                    <TopBar
                        onSidebarOpen={handleSidebarOpen}
                        onSidebarClose={handleSidebarClose}
                        openSidebar={openSidebar}
                        SignOutComponent={
                            null //TODO: figure out what SignoutComponent lol
                            // <SignoutComponent className="sign-out" />
                        }
                        handleSignout={handleSignout}
                        pageTitle={pageTitle}
                    />
                </header>
                {/* Sidebar */}
                <Sidebar
                    className="m-nav"
                    open={isSidebarCurrentlyOpen}
                    variant={isDesktop ? 'persistent' : 'temporary'}
                    onClose={handleSidebarClose}
                    openSidebar={openSidebar}
                >
                    <SidebarNav
                        className={'emp-mainLayout-nav'}
                        navLinks={currentNavLinks}
                        onSidebarOpen={handleSidebarOpen}
                        onSidebarClose={handleSidebarClose}
                        openSidebar={openSidebar}
                        currentPath={pathname}
                        setPageTitle={setPageTitle}
                    />
                    {user?.merchant?.show_alerts_banner && (
                        <DisputeDeflectorBanner />
                    )}
                    {themeVariantId === CLIENT_THEME_VARIANT.DISPUTE_PRO.id && (
                        <VASBanner />
                    )}
                    {showSidebarAccountSwitcher && (
                        <div
                            className="sidebar-bottom-container"
                            style={{
                                position: 'absolute',
                                bottom: 40,
                                left: 0,
                                width: '100%',
                            }}
                        >
                            <SidebarAccountSwitcher />
                        </div>
                    )}
                </Sidebar>
                <Box
                    component="main"
                    id={idDirectory.main}
                    className={`m-main ${!openSidebar ? 'sidebar-closed' : ''}`}
                    sx={(theme) => ({
                        flexGrow: 1,
                        height: '100%',
                        backgroundColor: theme.palette.background.default,
                        padding: theme.spacing(2),
                        [theme.breakpoints.up('lg')]: {
                            transition: theme.transitions.create('width', {
                                easing: theme.transitions.easing.sharp,
                                duration:
                                    theme.transitions.duration.enteringScreen,
                            }),
                            maxWidth: 'calc(100vw - 220px)',
                            '&.sidebar-closed': {
                                transition: theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration:
                                        theme.transitions.duration
                                            .leavingScreen,
                                }),
                                maxWidth: 'calc(100vw - 65px)',
                            },
                            // overflow: 'hidden',
                        },
                    })}
                >
                    <ErrorBoundary area="the main content">
                        {children}
                    </ErrorBoundary>
                </Box>
                <footer id={idDirectory.footer}>
                    <StandardFooter />
                </footer>
            </div>
            {showTermsAndConditionsModal && (
                <SabreTermsAndConditionsModal
                    headerTitle="Software and Services Agreement"
                    testId={`terms-and-conditions`}
                    disableBackdropClick
                    maxModalWidth="lg"
                    showCloseIcon
                />
            )}
        </div>
    )
}
export default MainLayout
